import React from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { entityDetailHref, entityToHref, type RelatedEntity } from '../utils/entity-utils';
import { handleEntityDetailActivation } from '../utils/entity-detail-activation';
import {
  opensEntityDetailInPlace,
  useOpenEntityDetailOptional,
} from '../contexts/OpenEntityDetailContext';
import { EntityTypeIcon, getEntityTypeLabelKey } from './EntityTypeIcon';
import { useLocale } from '../contexts/LocaleContext';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';

interface RelatedEntityLinkProps {
  entity: RelatedEntity;
  projectId: string;
  className?: string;
  onRemove?: () => void;
}

const chipClass =
  'inline-flex max-w-full min-w-0 items-center gap-1.5 overflow-hidden text-sm px-2.5 py-1 rounded-md bg-muted hover:bg-muted/80 text-foreground';

export const RelatedEntityLink: React.FC<RelatedEntityLinkProps> = ({
  entity,
  projectId,
  className,
  onRemove,
}) => {
  const { t } = useLocale();
  const entityDetail = useOpenEntityDetailOptional();
  const typeLabel = t(getEntityTypeLabelKey(entity.type));
  const href = entityDetailHref(entity, projectId);
  const openInPlace = Boolean(entityDetail && opensEntityDetailInPlace(entity.type) && href);
  const linkContent = (
    <>
      <EntityTypeIcon type={entity.type} typeLabel={typeLabel} />
      <span className="min-w-0 truncate">{entity.label}</span>
    </>
  );

  const activate = (event: React.MouseEvent): void => {
    handleEntityDetailActivation(event, entity, projectId, entityDetail?.openEntityDetail);
  };

  const entityControl = openInPlace ? (
    <button
      type="button"
      className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden bg-transparent p-0 text-left text-foreground"
      onClick={activate}
      onAuxClick={activate}
    >
      {linkContent}
    </button>
  ) : (
    <Link to={entityToHref(entity, projectId)} className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden no-underline text-foreground">
      {linkContent}
    </Link>
  );

  if (onRemove) {
    return (
      <span
        className={cn(chipClass, 'pr-0.5', className)}
        style={{ display: 'inline-flex', alignItems: 'center' }}
        title={entity.label}
      >
        {entityControl}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0 rounded-full opacity-70 hover:opacity-100"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          aria-label={t('Remove relationship')}
        >
          <X className="h-3 w-3" />
        </Button>
      </span>
    );
  }

  if (openInPlace) {
    return (
      <button
        type="button"
        className={cn(chipClass, 'cursor-pointer border-0 text-left', className)}
        onClick={activate}
        onAuxClick={activate}
        title={entity.label}
      >
        {linkContent}
      </button>
    );
  }

  return (
    <Link to={entityToHref(entity, projectId)} className={cn(chipClass, 'no-underline', className)} title={entity.label}>
      {linkContent}
    </Link>
  );
};
